import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { TrophyIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/solid'
import { weeklyProgramsAPI } from '../../services/api'

/**
 * Banner showing weekly score with comparison to best week.
 */
export function WeeklyScoreBanner({ programId }) {
  const { t } = useTranslation()

  const { data: score, isLoading, error } = useQuery({
    queryKey: ['programScore', programId],
    queryFn: () => weeklyProgramsAPI.getScore(programId),
    enabled: !!programId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false
  })

  const { data: rankings } = useQuery({
    queryKey: ['scoreRankings'],
    queryFn: () => weeklyProgramsAPI.getScoreRankings(),
    staleTime: 5 * 60 * 1000
  })

  if (isLoading) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
      </div>
    )
  }

  if (error || !score?.data) {
    return null // Silently fail if score not available
  }

  const scoreData = score.data
  const rankingsData = rankings?.data
  const isBestWeek = scoreData.is_personal_best
  const totalScore = parseFloat(scoreData.total_score || 0)
  const scoreChange = parseFloat(scoreData.score_vs_prev_week_percent || 0)

  return (
    <div
      className={`
        rounded-lg p-4 border
        ${isBestWeek
          ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200'
          : 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200'
        }
      `}
    >
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        {/* Current week score */}
        <div>
          <div className="flex items-center gap-2">
            {isBestWeek && (
              <TrophyIcon className="h-5 w-5 text-yellow-500" />
            )}
            <h3 className="font-semibold text-gray-900">
              {isBestWeek
                ? t('workouts.scoring.bestWeek', 'Personal Best Week!')
                : t('workouts.scoring.weekScore', 'Week Score')
              }
            </h3>
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-3xl font-bold text-indigo-600">
              {totalScore.toFixed(0)}
            </span>
            <span className="text-sm text-gray-500">/ 100 pts</span>
            {scoreChange !== 0 && (
              <span
                className={`
                  flex items-center text-sm font-medium
                  ${scoreChange > 0 ? 'text-green-600' : 'text-red-600'}
                `}
              >
                {scoreChange > 0 ? (
                  <ArrowTrendingUpIcon className="h-4 w-4 mr-0.5" />
                ) : (
                  <ArrowTrendingDownIcon className="h-4 w-4 mr-0.5" />
                )}
                {scoreChange > 0 ? '+' : ''}{scoreChange.toFixed(1)}%
              </span>
            )}
          </div>
        </div>

        {/* Best week info */}
        {rankingsData?.best_week && !isBestWeek && (
          <div className="text-right">
            <p className="text-xs text-gray-500">
              {t('workouts.scoring.allTimeBest', 'All-time best')}
            </p>
            <p className="font-semibold text-gray-900">
              {t('workouts.scoring.weekNumber', 'Week {{week}}, {{year}}', {
                week: rankingsData.best_week.week,
                year: rankingsData.best_week.year
              })}
            </p>
            <p className="text-lg font-bold text-gray-700">
              {parseFloat(rankingsData.best_week.score || 0).toFixed(0)} pts
            </p>
          </div>
        )}

        {/* Trend info */}
        {rankingsData && (
          <div className="hidden md:block text-right">
            <p className="text-xs text-gray-500">
              {t('workouts.scoring.trend', 'Trend')}
            </p>
            <TrendBadge trend={rankingsData.recent_trend} />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Small badge showing trend direction.
 */
function TrendBadge({ trend }) {
  const { t } = useTranslation()

  const config = {
    improving: {
      label: t('workouts.scoring.improving', 'Improving'),
      className: 'bg-green-100 text-green-800',
      icon: ArrowTrendingUpIcon
    },
    declining: {
      label: t('workouts.scoring.declining', 'Declining'),
      className: 'bg-red-100 text-red-800',
      icon: ArrowTrendingDownIcon
    },
    stable: {
      label: t('workouts.scoring.stable', 'Stable'),
      className: 'bg-gray-100 text-gray-800',
      icon: null
    },
    neutral: {
      label: t('workouts.scoring.neutral', 'New'),
      className: 'bg-blue-100 text-blue-800',
      icon: null
    }
  }

  const { label, className, icon: Icon } = config[trend] || config.neutral

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </span>
  )
}

export default WeeklyScoreBanner
